import { useEffect, useMemo, useState } from 'react';
import { Dispatch, bindActionCreators } from 'redux';
import { FormattedMessage, injectIntl, useIntl } from 'react-intl';
import { connect } from 'react-redux';
import { withRouter } from 'react-router';
import { RootState } from '../../reducers';
import * as actions from '../../actions/generalActions';
import { axiosCaller } from '../../utils/apiCaller';
import { toast } from 'react-toastify';
import { t } from '../../i18n';
import './offerSettings.scss';

interface OptionLabel {
    titleKey?: string;
    descriptionKey?: string;
}

interface SettingDefinition {
    key: string;
    type: 'enum' | 'boolean' | 'integer' | 'string';
    default: any;
    options?: any[];
    labelKey?: string;
    descriptionKey?: string;
    permission?: string | null;
    metadata?: {
        affects?: string;
        optionLabels?: Record<string, OptionLabel>;
        [k: string]: any;
    };
}

interface ApiPayload {
    namespace: string;
    definitions: SettingDefinition[];
    values: Record<string, any>;
}

interface Props {
    actions: any;
}

const ProductionSettings = (_props: Props) => {
    const [definitions, setDefinitions] = useState<SettingDefinition[]>([]);
    const [savedValues, setSavedValues] = useState<Record<string, any>>({});
    const [dirtyValues, setDirtyValues] = useState<Record<string, any>>({});
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    useEffect(() => { loadSettings(); }, []);

    const loadSettings = () => {
        setLoading(true);
        axiosCaller('GET', 'admin/production-settings').then((res: any) => {
            const payload: ApiPayload = res?.data || { namespace: 'production', definitions: [], values: {} };
            setDefinitions(payload.definitions || []);
            setSavedValues(payload.values || {});
            setDirtyValues(payload.values || {});
            setLoading(false);
        }).catch(() => {
            setLoading(false);
            toast.error(t('admin.toast.somethingWentWrong'));
        });
    };

    const isDirty = useMemo(
        () => Object.keys(dirtyValues).some(k => dirtyValues[k] !== savedValues[k]),
        [dirtyValues, savedValues]
    );

    const handleChange = (key: string, value: any) => {
        setDirtyValues(prev => ({ ...prev, [key]: value }));
    };

    const handleSave = () => {
        if (!isDirty || saving) return;
        setSaving(true);
        axiosCaller('POST', 'admin/production-settings', { values: dirtyValues }).then((res: any) => {
            const newValues = res?.data?.values || dirtyValues;
            setSavedValues(newValues);
            setDirtyValues(newValues);
            setSaving(false);
            toast.success(t('admin.productionSettings.savedToast'));
        }).catch((err: any) => {
            setSaving(false);
            toast.error(err?.response?.data?.message || t('admin.toast.somethingWentWrong'));
        });
    };

    return (
        <div style={{ padding: 20, width: '100%' }}>
            <div className="os-page">
                <div className="os-page-header">
                    <div>
                        <h1 className="os-page-title">
                            <FormattedMessage id="admin.productionSettings.pageTitle" defaultMessage="Preferencat e prodhimit" />
                        </h1>
                        <p className="os-page-subtitle">
                            <FormattedMessage id="admin.productionSettings.pageSubtitle" defaultMessage="Konfiguroni se si funksionon moduli i prodhimit për kompaninë tuaj" />
                        </p>
                    </div>
                </div>

                {loading ? (
                    <div className="os-card">
                        <div className="os-card-body">
                            <p className="os-empty-text"><FormattedMessage id="common.loading" defaultMessage="Duke ngarkuar..." /></p>
                        </div>
                    </div>
                ) : definitions.length === 0 ? (
                    <div className="os-card">
                        <div className="os-card-body">
                            <p className="os-empty-text">
                                <FormattedMessage id="admin.productionSettings.empty" defaultMessage="Asnjë preferencë e disponueshme" />
                            </p>
                        </div>
                    </div>
                ) : (
                    <>
                        {definitions.map(def => (
                            <SettingCard
                                key={def.key}
                                definition={def}
                                value={dirtyValues[def.key]}
                                onChange={(value: any) => handleChange(def.key, value)}
                            />
                        ))}

                        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 16 }}>
                            <button
                                type="button"
                                onClick={handleSave}
                                disabled={!isDirty || saving}
                                style={{
                                    padding: '10px 24px',
                                    borderRadius: 8,
                                    border: 'none',
                                    background: (!isDirty || saving) ? '#cbd5e1' : '#1e3a5f',
                                    color: '#fff',
                                    fontSize: 14,
                                    fontWeight: 600,
                                    cursor: (!isDirty || saving) ? 'default' : 'pointer',
                                    minWidth: 140,
                                    transition: 'background 0.15s',
                                }}
                            >
                                <FormattedMessage id="common.action.save" defaultMessage="Ruaj" />
                            </button>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

// One card per registered setting. Switches on definition.type for the widget.
interface SettingCardProps {
    definition: SettingDefinition;
    value: any;
    onChange: (value: any) => void;
}

const SettingCard: React.FC<SettingCardProps> = ({ definition, value, onChange }) => {
    const intl = useIntl();

    const cardTitle = definition.labelKey
        ? intl.formatMessage({ id: definition.labelKey, defaultMessage: definition.key })
        : definition.key;
    const cardDesc = definition.descriptionKey
        ? intl.formatMessage({ id: definition.descriptionKey, defaultMessage: '' })
        : '';

    return (
        <div className="os-card">
            <div className="os-card-header">
                <div>
                    <h2>{cardTitle}</h2>
                    {cardDesc && <p className="os-card-desc">{cardDesc}</p>}
                </div>
            </div>
            <div className="os-card-body">
                {definition.type === 'enum' ? (
                    <EnumRadioCards definition={definition} value={value} onChange={onChange} />
                ) : (
                    <p className="os-empty-text">
                        <FormattedMessage
                            id="admin.productionSettings.unsupportedType"
                            defaultMessage="Lloji i kësaj preference nuk mbështetet ende."
                        />
                    </p>
                )}
            </div>
        </div>
    );
};

// Radio-card widget for enum settings. Auto-fit grid: side-by-side on desktop,
// stacks on mobile (narrowest tier set to 280px).
interface EnumRadioCardsProps {
    definition: SettingDefinition;
    value: any;
    onChange: (value: any) => void;
}

const EnumRadioCards: React.FC<EnumRadioCardsProps> = ({ definition, value, onChange }) => {
    const intl = useIntl();
    const optionLabels = definition.metadata?.optionLabels || {};

    const titleFor = (option: any): string => {
        const meta = optionLabels[String(option)];
        return meta?.titleKey
            ? intl.formatMessage({ id: meta.titleKey, defaultMessage: String(option) })
            : String(option);
    };

    const descriptionFor = (option: any): string => {
        const meta = optionLabels[String(option)];
        return meta?.descriptionKey
            ? intl.formatMessage({ id: meta.descriptionKey, defaultMessage: '' })
            : '';
    };

    return (
        <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
            gap: 12,
        }}>
            {(definition.options || []).map(option => {
                const isSelected = value === option;
                const description = descriptionFor(option);
                return (
                    <button
                        type="button"
                        key={String(option)}
                        onClick={() => onChange(option)}
                        style={{
                            position: 'relative',
                            textAlign: 'left',
                            padding: '16px 18px',
                            borderRadius: 10,
                            border: isSelected ? '2px solid #1e3a5f' : '2px solid #e2e8f0',
                            background: isSelected ? '#eef3f9' : '#fff',
                            cursor: 'pointer',
                            transition: 'border-color 0.15s, background 0.15s',
                            minHeight: 110,
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 6,
                            outline: 'none',
                            font: 'inherit',
                        }}
                        onMouseEnter={(e) => {
                            if (!isSelected) e.currentTarget.style.borderColor = '#94a3b8';
                        }}
                        onMouseLeave={(e) => {
                            if (!isSelected) e.currentTarget.style.borderColor = '#e2e8f0';
                        }}
                    >
                        {isSelected && (
                            <div style={{
                                position: 'absolute',
                                top: 12,
                                right: 12,
                                width: 22,
                                height: 22,
                                borderRadius: '50%',
                                background: '#1e3a5f',
                                color: '#fff',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                            }}>
                                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                                    <polyline points="20 6 9 17 4 12" />
                                </svg>
                            </div>
                        )}

                        <div style={{
                            fontSize: 15,
                            fontWeight: 600,
                            color: '#0f172a',
                            paddingRight: isSelected ? 32 : 0,
                        }}>
                            {titleFor(option)}
                        </div>
                        {description && (
                            <div style={{ fontSize: 12, color: '#64748b', lineHeight: 1.5 }}>
                                {description}
                            </div>
                        )}
                    </button>
                );
            })}
        </div>
    );
};

function mapStateToProps(state: RootState, ownProps: any) {
    return { ...ownProps, ...state.app };
}

function mapDispatchToProps(dispatch: Dispatch<actions.ACTION>) {
    return {
        actions: bindActionCreators(actions as any, dispatch),
    };
}

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(injectIntl(ProductionSettings as any)));
